/**
 * Usage statistics for the gestionale with Umami, self-hosted and served from
 * this origin (M17, section E).
 *
 * The same shape as the site's (apps/site/src/stats.ts): the tracker sets no
 * cookie, comes from our own origin, and every payload passes through
 * `scrubStatsPayload` by the tracker's `data-before-send` hook. What differs is
 * who is looking: these are signed-in people, so the reduction is stricter and
 * nothing identifies an agency or a person.
 *
 * - The page is a route template (`/properties/:id`), never a URL: no record id,
 *   no query (a list keeps its search in the query), no hash.
 * - The title and the referrer are empty: inside the app the first can hold a
 *   record's name and the second is the previous page.
 * - Events carry names and categorical properties only — a form's key, a
 *   field's schema name, an HTTP status. Never a value anyone typed.
 *
 * Who did what, and for which agency, is not asked of Umami: that is the
 * server's product events, tied to the tenant (M17, section A). There is no
 * `umami.identify` here and no agency or user id on any event, because a stable
 * id would undo the daily rotation of the visitor identifier.
 */

/** The window property the tracker's `data-before-send` names. */
export const BEFORE_SEND = 'aimmoStatsBeforeSend';

/** Routed to Umami by the ingress, together with `/stats/api/send`; nothing else is. */
export const TRACKER_PATH = '/stats/script.js';

import type { KitTelemetryEvent } from '@aimmo/ui';

type Payload = Record<string, unknown>;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMERIC = /^\d+$/;

/** A path with every record id in it replaced; no query, no hash. */
function pathOf(raw: string, base: string): string | undefined {
  try {
    const { pathname } = new URL(raw, base);
    return pathname
      .split('/')
      .map((segment) => (UUID.test(segment) || NUMERIC.test(segment) ? ':id' : segment))
      .join('/');
  } catch {
    return undefined;
  }
}

/**
 * A router's `fullPath` as the page the statistics see: `/properties/$id/edit`
 * becomes `/properties/:id/edit`. Whatever the parameter is called, it is `:id`.
 */
export function routeTemplate(fullPath: string): string {
  return fullPath.replace(/\$[A-Za-z0-9_]+/g, ':id');
}

/**
 * What the tracker may send. `base` is the page's own URL, so a relative value
 * resolves against it.
 */
export function scrubStatsPayload(payload: Payload, base: string): Payload {
  const scrubbed: Payload = { ...payload };
  if (typeof payload['url'] === 'string') {
    const path = pathOf(payload['url'], base);
    if (path === undefined) delete scrubbed['url'];
    else scrubbed['url'] = path;
  }
  if ('title' in payload) scrubbed['title'] = '';
  if ('referrer' in payload) scrubbed['referrer'] = '';
  return scrubbed;
}

const ATTRIBUTE = /^[a-z0-9-]+$/;

/**
 * The tracker's script tag, or nothing.
 *
 * Nothing unless a website id is configured at build time: development without
 * one, the e2e suite and any build without it load no tracker at all.
 * `data-domains` limits counting to the host the build was made for.
 *
 * `data-auto-track` is off: the tracker would read the URL itself, and it cannot
 * know which part of it is a record id. The router tells it, per page, through
 * `trackPage`.
 */
export function statsScriptTag(websiteId: string | undefined, appUrl: string): string {
  const id = websiteId?.trim();
  if (!id) return '';
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)) {
    throw new Error(`UMAMI_APP_WEBSITE_ID must be a UUID — got ${JSON.stringify(websiteId)}`);
  }
  const host = new URL(appUrl).hostname;
  const attributes: [string, string][] = [
    ['src', TRACKER_PATH],
    ['data-website-id', id.toLowerCase()],
    ['data-domains', host],
    ['data-auto-track', 'false'],
    ['data-exclude-hash', 'true'],
    ['data-do-not-track', 'true'],
    ['data-before-send', BEFORE_SEND],
  ];
  const rendered = attributes
    .map(([name, value]) => {
      if (!ATTRIBUTE.test(name)) throw new Error(`bad attribute ${name}`);
      return `${name}="${value}"`;
    })
    .join(' ');
  return `<script defer ${rendered}></script>`;
}

type EventData = Record<string, string | number>;

interface StatsWindow {
  location: { href: string };
  umami?: {
    track: (nameOrBuild: string | ((payload: Payload) => Payload), data?: EventData) => unknown;
  };
  [BEFORE_SEND]?: (type: string, payload: Payload) => Payload;
}

/** Installs the scrubbing hook. Harmless when no tracker is on the page. */
export function installStats(target: StatsWindow): void {
  target[BEFORE_SEND] = (_type, payload) => scrubStatsPayload(payload, target.location.href);
}

/** A page view, named by its route template rather than by the URL it was reached at. */
export function trackPage(target: StatsWindow, fullPath: string): void {
  try {
    const url = routeTemplate(fullPath);
    void target.umami?.track((payload) => ({ ...payload, url, title: '', referrer: '' }));
  } catch {
    // Statistics never get in the way of the screen.
  }
}

// ---------------------------------------------------------------------------
// The catalogue
// ---------------------------------------------------------------------------

export type Viewport = 'mobile' | 'tablet' | 'desktop';
export type Display = 'browser' | 'standalone';
export type SecondsBand = '<10' | '10-60' | '60-300' | '300+';
export type ErrorKind = 'network' | 'forbidden' | 'not-found' | 'server' | 'render';

/** Things done on a list that change no route. No value, no query, no filter. */
export type StatsAction = 'search' | 'filter' | 'sort';

/**
 * Every event, and the properties it may carry. A closed set, like
 * `PRODUCT_EVENTS`: what is measured is reviewed here, not scattered as string
 * literals. Each property is a short category or a number from a fixed scale.
 */
export type StatsEvent =
  | { name: 'app-opened'; data: { display: Display; viewport: Viewport } }
  | { name: 'form-opened'; data: { form: string } }
  | { name: 'form-invalid'; data: { form: string; field: string } }
  | { name: 'form-submitted'; data: { form: string } }
  | {
      name: 'form-abandoned';
      data: {
        form: string;
        reason: 'left' | 'cancelled';
        /** How many of the form's fields had been touched. */
        touched: number;
        seconds: SecondsBand;
      };
    }
  | {
      name: 'error-shown';
      data: { kind: ErrorKind; status: number; endpoint: string; area: string };
    }
  | { name: 'assistant-question'; data: { n: string } }
  | { name: 'action'; data: { area: string; name: StatsAction } };

/**
 * What the resource kit reports (`setKitTelemetry` in @aimmo/ui), as the
 * statistics' own events: the one place where a field gets its position
 * stripped and a duration its band.
 */
export function fromKitEvent(event: KitTelemetryEvent): StatsEvent {
  switch (event.type) {
    case 'form-opened':
    case 'form-submitted':
      return { name: event.type, data: { form: event.form } };
    case 'form-invalid':
      return { name: 'form-invalid', data: { form: event.form, field: fieldKey(event.field) } };
    case 'form-abandoned':
      return {
        name: 'form-abandoned',
        data: {
          form: event.form,
          reason: event.reason,
          touched: event.touched,
          seconds: secondsBand(event.seconds),
        },
      };
    case 'list-action':
      return { name: 'action', data: { area: event.area, name: event.action } };
  }
}

/** A page load reports at most this many errors: a broken endpoint polled by a screen is one fact. */
const ERRORS_PER_LOAD = 20;
let errorsSent = 0;

/** A named event, if the tracker is there. It never throws. */
export function trackEvent(target: StatsWindow, event: StatsEvent): void {
  try {
    if (event.name === 'error-shown') {
      if (errorsSent >= ERRORS_PER_LOAD) return;
      errorsSent += 1;
    }
    void target.umami?.track(event.name, event.data);
  } catch {
    // Statistics never get in the way of the screen.
  }
}

// ---------------------------------------------------------------------------
// Categories: the scales the properties above are drawn from
// ---------------------------------------------------------------------------

/** Time on a form, banded: a duration to the second is a fingerprint, a band is not. */
export function secondsBand(seconds: number): SecondsBand {
  if (seconds < 10) return '<10';
  if (seconds < 60) return '10-60';
  if (seconds < 300) return '60-300';
  return '300+';
}

/**
 * Which question of a conversation this is, capped: "the 3rd" tells how deep
 * people go, and an unbounded count would tell one conversation from another.
 */
export function questionOrdinal(n: number): string {
  return n >= 10 ? '10+' : String(Math.max(1, n));
}

/** A field by its schema key. A position in an array is not part of the name. */
export function fieldKey(name: string): string {
  return name
    .split('.')
    .filter((part) => !NUMERIC.test(part))
    .join('.');
}

/**
 * The kind of failure a status is, for the ones a person is shown. The others
 * are handled where they happen — a form writes a 422 onto its fields, a dead
 * session goes to the login — and counting them here would only duplicate.
 */
export function errorKindOf(status: number): ErrorKind | undefined {
  if (status === 0) return 'network';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not-found';
  if (status >= 500) return 'server';
  return undefined;
}

/**
 * A request as the statistics name it: the contract's path template — never the
 * URL asked for, which holds ids and a query — and the area it belongs to.
 * `/api` is where the app is mounted, not part of either.
 */
export function endpointOf(method: string, routePath: string): { endpoint: string; area: string } {
  const template = routeTemplate(routePath.replace(/^\/api(?=\/|$)/, '') || '/');
  const area = template.split('/').find((part) => part !== '') ?? '';
  return { endpoint: `${method.toUpperCase()} ${template}`, area };
}

/** The app's own breakpoints: compact below 48rem (768px) and `lg` from 1024px. */
export function viewportOf(width: number): Viewport {
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
}
